import React, { useEffect, useState } from "react";
import { fetchSettings, updateSettings, geocodeAddress } from "../lib/api";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Save, Cpu, Clock, Sparkles, Bus, MapPin, Loader2 } from "lucide-react";
import { ContactsManager } from "../components/ContactsManager";
import { toast } from "sonner";

export default function Settings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [geocoding, setGeocoding] = useState(false);

  useEffect(() => {
    fetchSettings().then(setS);
  }, []);

  if (!s) {
    return (
      <div className="max-w-[900px] mx-auto space-y-4">
        <Skeleton className="h-8 w-48 bg-zinc-900/50" />
        <Skeleton className="h-40 bg-zinc-900/50" />
      </div>
    );
  }

  const handleSave = async () => {
    try {
      setSaving(true);
      const next = await updateSettings({
        enable_llm: s.enable_llm,
        auto_import_dcc: s.auto_import_dcc,
        scan_concurrency: Number(s.scan_concurrency) || 4,
        scan_timeout_seconds: Number(s.scan_timeout_seconds) || 12,
        scan_priority_limit: Number(s.scan_priority_limit) || 50,
        bus_depot_address: s.bus_depot_address || "",
        bus_depot_latitude: s.bus_depot_latitude,
        bus_depot_longitude: s.bus_depot_longitude,
      });
      setS(next);
      toast.success("Settings saved");
    } catch (e) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleGeocode = async () => {
    const q = (s.bus_depot_address || "").trim();
    if (q.length < 5) {
      toast.error("Type an address first");
      return;
    }
    try {
      setGeocoding(true);
      const r = await geocodeAddress(q);
      if (r.ok) {
        setS({
          ...s,
          bus_depot_address: r.display_name || q,
          bus_depot_latitude: r.latitude,
          bus_depot_longitude: r.longitude,
        });
        toast.success("Address found", {
          description: `${r.latitude.toFixed(4)}, ${r.longitude.toFixed(4)}`,
        });
      } else if ((r.error || "").includes("429")) {
        toast.error("Geocoder is busy", {
          description: "Try again in a few seconds — OpenStreetMap throttles requests.",
        });
      } else {
        toast.error("Geocode failed", { description: r.error || "No match" });
      }
    } catch {
      toast.error("Geocode request failed");
    } finally {
      setGeocoding(false);
    }
  };

  return (
    <div className="max-w-[900px] mx-auto space-y-5 fade-up">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">Settings</h2>
        <p className="text-sm text-zinc-500 mt-1">
          Configure how the scanner discovers and scores opening dates.
        </p>
      </div>

      <div className="panel p-6 space-y-5">
        <div className="flex items-start justify-between gap-6">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-md bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mt-0.5">
              <Sparkles className="w-4 h-4 text-emerald-400" />
            </div>
            <div>
              <div className="text-sm font-medium text-zinc-50">
                Auto-import DCC license list
              </div>
              <div className="text-xs text-zinc-500 mt-1 max-w-md leading-relaxed">
                When enabled, every <strong className="text-zinc-300">Run Scan</strong>{" "}
                fetches the latest California DCC license dataset, merges new
                storefront retailers into your database (no duplicates), and
                flags any dispensary whose license has gone{" "}
                <span className="text-amber-300">Suspended / Revoked / Expired</span>.
              </div>
            </div>
          </div>
          <Switch
            data-testid="setting-auto-import-dcc"
            checked={s.auto_import_dcc}
            onCheckedChange={(v) => setS({ ...s, auto_import_dcc: v })}
          />
        </div>

        <div className="h-px bg-zinc-800" />

        <div className="flex items-start justify-between gap-6">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-md bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mt-0.5">
              <Sparkles className="w-4 h-4 text-emerald-400" />
            </div>
            <div>
              <div className="text-sm font-medium text-zinc-50">
                Claude assist (LLM)
              </div>
              <div className="text-xs text-zinc-500 mt-1 max-w-md leading-relaxed">
                When enabled, Claude reviews ambiguous evidence and helps pick the
                most likely original grand-opening date. Disable to use HTTP
                heuristics only.
              </div>
            </div>
          </div>
          <Switch
            data-testid="setting-enable-llm"
            checked={s.enable_llm}
            onCheckedChange={(v) => setS({ ...s, enable_llm: v })}
          />
        </div>

        <div className="h-px bg-zinc-800" />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div>
            <label className="text-[10px] uppercase tracking-[0.12em] font-mono text-zinc-500 flex items-center gap-1.5 mb-2">
              <Cpu className="w-3.5 h-3.5" /> Scan concurrency
            </label>
            <Input
              data-testid="setting-concurrency"
              type="number"
              min={1}
              max={16}
              value={s.scan_concurrency}
              onChange={(e) =>
                setS({ ...s, scan_concurrency: Number(e.target.value) })
              }
              className="bg-zinc-950 border-zinc-800 text-zinc-100 font-mono"
            />
            <div className="text-xs text-zinc-500 mt-1.5">
              Parallel page fetches per scan run.
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.12em] font-mono text-zinc-500 flex items-center gap-1.5 mb-2">
              <Clock className="w-3.5 h-3.5" /> Timeout (seconds)
            </label>
            <Input
              data-testid="setting-timeout"
              type="number"
              min={3}
              max={60}
              value={s.scan_timeout_seconds}
              onChange={(e) =>
                setS({ ...s, scan_timeout_seconds: Number(e.target.value) })
              }
              className="bg-zinc-950 border-zinc-800 text-zinc-100 font-mono"
            />
            <div className="text-xs text-zinc-500 mt-1.5">
              Max time per page fetch.
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.12em] font-mono text-zinc-500 flex items-center gap-1.5 mb-2">
              <Cpu className="w-3.5 h-3.5" /> Per-scan opening-date limit
            </label>
            <Input
              data-testid="setting-priority-limit"
              type="number"
              min={10}
              max={1500}
              value={s.scan_priority_limit}
              onChange={(e) =>
                setS({ ...s, scan_priority_limit: Number(e.target.value) })
              }
              className="bg-zinc-950 border-zinc-800 text-zinc-100 font-mono"
            />
            <div className="text-xs text-zinc-500 mt-1.5 leading-relaxed">
              Dispensaries enriched per Run Scan (after DCC import). Priority:
              records missing an opening date OR website OR Instagram first.
              <br />
              <span className="text-zinc-400">
                Set to <span className="font-mono text-emerald-300">1500</span>{" "}
                to sweep every active dispensary in one click — about{" "}
                <span className="font-mono">60&nbsp;min</span> at concurrency 4,{" "}
                <span className="font-mono">~$1-5 with LLM on</span>, $0 with
                LLM off.
              </span>
            </div>
          </div>
        </div>

        <div className="pt-2">
          <Button
            data-testid="save-settings-btn"
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50 rounded-md"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving…" : "Save settings"}
          </Button>
        </div>
      </div>

      <ContactsManager />

      <div id="bus-depot" className="panel p-6 space-y-4 scroll-mt-20">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-md bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mt-0.5">
            <Bus className="w-4 h-4 text-amber-300" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-medium text-zinc-50">
              Bus Depot
            </h3>
            <p className="text-xs text-zinc-500 mt-1 max-w-md leading-relaxed">
              Where the bus-event busses are kept. When a route is built with{" "}
              <span className="text-amber-300">Start &amp; end at depot</span>{" "}
              enabled, the Google Maps route is bookended by this address —
              first stop, last stop. Click <span className="font-mono">Find on map</span>{" "}
              after editing to lock in GPS coordinates.
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-[10px] uppercase tracking-wider font-mono text-zinc-400">
            Depot address
          </label>
          <div className="flex items-stretch gap-2">
            <Input
              data-testid="setting-depot-address"
              value={s.bus_depot_address || ""}
              onChange={(e) =>
                setS({ ...s, bus_depot_address: e.target.value })
              }
              placeholder="e.g. 1929 E St Andrew Pl, Santa Ana, CA"
              className="bg-zinc-950 border-zinc-800 text-zinc-100 font-mono text-sm flex-1"
            />
            <Button
              data-testid="setting-depot-geocode"
              onClick={handleGeocode}
              disabled={geocoding}
              variant="outline"
              className="border-zinc-800 bg-zinc-900 hover:bg-zinc-800 text-zinc-100 rounded-md whitespace-nowrap"
            >
              {geocoding ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <MapPin className="w-3.5 h-3.5 mr-1.5" />
              )}
              Find on map
            </Button>
          </div>
          {(s.bus_depot_latitude || s.bus_depot_longitude) && (
            <div
              data-testid="setting-depot-coords"
              className="text-[11px] font-mono text-zinc-500 inline-flex items-center gap-1.5"
            >
              <MapPin className="w-3 h-3 text-emerald-400" />
              {Number(s.bus_depot_latitude || 0).toFixed(4)},{" "}
              {Number(s.bus_depot_longitude || 0).toFixed(4)}
            </div>
          )}
        </div>
      </div>

      <div className="panel p-6">
        <div className="text-[10px] uppercase tracking-[0.12em] font-mono text-zinc-500 mb-3">
          About
        </div>
        <p className="text-sm text-zinc-400 leading-relaxed">
          JackTrack is a local research tool. All data lives in your own
          database. Every estimated opening date is backed by{" "}
          <span className="text-zinc-200">evidence snippets and source URLs</span>{" "}
          — dates are never fabricated.
        </p>
      </div>
    </div>
  );
}
